import { createSelector } from '@reduxjs/toolkit';
import { keyBy, sortBy } from 'lodash-es';

import type { MetadataMetadataFieldSchemaPatched } from '@iconik-io/iconik-api/src/schema_patched/metadata_field';
import {
  OBJECT_TYPE_ASSETS,
  OBJECT_TYPE_COLLECTIONS,
} from '@iconik-io/iconik-api/src/types/object_types';
import {
  asSearchFields,
  DEFAULT_DISPLAY_FIELDS,
  FIELD_KEYFRAMES,
  FIELD_MEDIA_TYPE,
  FIELD_TITLE,
  isNonCustomFieldType,
  SEARCH_SYSTEM_FIELDS,
  SEARCH_SYSTEM_FIELDS_BY_NAME,
  type SearchField,
} from '@iconik-io/shared-lib/src/constants/search';
import type { SearchItemEntity } from '@iconik-io/shared-lib/src/redux/entities/normalized/search_item';
import type { UserRoles } from '@iconik-io/shared-lib/src/utils/roles';

import { EXTRASMALL_SCREEN } from '../../constants/breakpoints';
import { SHARE_COLLECTION_PAGE_ROUTE } from '../../constants/routes';
import {
  DEFAULT_MULTI_SELECT_FIELD_NAMES,
  MULTI_SELECT_TEXT_FIELD_TYPES,
} from '../../constants/search_mappings';
import type { AppState } from '../../store/types';
import type { Column } from '../../ui/table/types';

type Props = {
  items: SearchItemEntity[];
  draggedItems: SearchItemEntity[];
  roles: UserRoles;
};

const getItems = (props: Props) => props.items;
const getDraggedItems = (props: Props) => props.draggedItems;
const getRoles = (props: Props) => props.roles;

export const selectAllDropTargetIds = createSelector(
  getItems,
  getDraggedItems,
  getRoles,
  (items, draggedItems, roles) => {
    let allDropTargetIds: string[] = [];

    if (draggedItems && draggedItems.length) {
      const draggedIds = draggedItems.map((item) => item.id);
      const [draggedItem] = draggedItems;

      allDropTargetIds = items.reduce(
        (acc: string[], item: SearchItemEntity) => {
          if (!draggedIds.includes(item.id)) {
            // For drop on collections
            if (item.object_type === OBJECT_TYPE_COLLECTIONS) {
              acc.push(item.id);
            } else if (item.object_type === OBJECT_TYPE_ASSETS) {
              // When there is one asset in drag which doesn't have versions - we can use it for merge
              // All proxies of that asset has to be processed, assets without proxies are still available for merge
              const proxies = draggedItem.proxies ?? [];
              const allProxiesProcessed = proxies.every(
                (proxy) => proxy.status === 'CLOSED',
              );
              if (
                draggedItems.length === 1 &&
                draggedItem.versions?.length === 1 &&
                draggedItem.object_type === OBJECT_TYPE_ASSETS &&
                roles.canWriteVersions &&
                allProxiesProcessed
              ) {
                acc.push(item.id);
              }
            }
          }
          return acc;
        },
        [],
      );
    }

    return allDropTargetIds;
  },
);

const getSearchFieldFromMetadataField = (
  item: MetadataMetadataFieldSchemaPatched,
): SearchField | undefined => {
  // A separator is a layout element of a metadata view, not a column.
  if (!isNonCustomFieldType(item.field_type)) {
    return undefined;
  }
  return {
    name: `metadata.${item.name}`,
    type: item.field_type,
    label: item.label ?? '',
    facetable: item.use_as_facet ?? undefined,
    sortable: true,
  };
};

/** Drops the fields that are not columns, such as a view separator. */
export const toSearchFields = (
  fields: MetadataMetadataFieldSchemaPatched[],
): SearchField[] =>
  fields.flatMap((field) => getSearchFieldFromMetadataField(field) ?? []);

export const getMetadataUserFields = (state: AppState) =>
  state.entities.metadata_user_fields;

const getMetadataEntityFields = (state: AppState) =>
  state.entities.metadata_fields;

export const selectMetadataMultiSelectFieldNames = createSelector(
  getMetadataUserFields,
  getMetadataEntityFields,
  (userMetadataFields, metadataFields): ReadonlySet<string> => {
    const allFields = { ...userMetadataFields, ...metadataFields };
    const extraNames = Object.values(allFields)
      .filter(
        (field) =>
          field.field_type === 'drop_down' ||
          field.field_type === 'tag_cloud' ||
          MULTI_SELECT_TEXT_FIELD_TYPES.has(field.field_type),
      )
      .map((field) => `metadata.${field.name}`);
    if (extraNames.length === 0) {
      return DEFAULT_MULTI_SELECT_FIELD_NAMES;
    }
    return new Set([...DEFAULT_MULTI_SELECT_FIELD_NAMES, ...extraNames]);
  },
);

export const selectUserMetadataSearchFields = createSelector(
  getMetadataUserFields,
  (userMetadataFields) => {
    const metadataFields = toSearchFields(Object.values(userMetadataFields));

    return sortBy(metadataFields, 'label');
  },
);

const getIsSharedCollection = (state: AppState) =>
  state.routeParams.route === SHARE_COLLECTION_PAGE_ROUTE;

export const selectAvailableSearchFields = createSelector(
  selectUserMetadataSearchFields,
  (metadataFields) => SEARCH_SYSTEM_FIELDS.concat(metadataFields),
);

export const selectAvailableSearchFieldsMap = createSelector(
  selectAvailableSearchFields,
  (availableFields) =>
    Object.fromEntries(
      availableFields.map((item) => [item.originFieldName ?? item.name, item]),
    ),
);

export const getMergedSearchDisplayFields = (state: AppState) =>
  asSearchFields(state.settings.merged.search_display_fields);

const getSearchDisplayFields = (state: AppState) =>
  asSearchFields(state.pages.search.view.currentDisplayFields);

const getMetadataViews = (state: AppState) => state.entities.metadata_views;

const getShareMetadataViews = (state: AppState) =>
  state.share?.share?.metadata_views;

export const selectShareMetadataFields = createSelector(
  getMetadataViews,
  getShareMetadataViews,
  (metadataViews, shareMetadataViews) =>
    shareMetadataViews
      ? shareMetadataViews.flatMap(
          (item) => metadataViews[item]?.view_fields || [],
        )
      : [],
);

export const selectVisibleSearchFields = createSelector(
  getSearchDisplayFields,
  getIsSharedCollection,
  selectShareMetadataFields,
  (
    searchDisplayFields,
    isSharedCollection,
    shareMetadataFields,
  ): SearchField[] => {
    const displayFields = searchDisplayFields ?? [];

    if (displayFields.length === 0) {
      return DEFAULT_DISPLAY_FIELDS;
    }

    // From settings, we pick order of active fields and their width,
    // from static defined system field we pick all the rest properties.
    // We also pick complete metadata fields from settings as they are not
    // statically defined.

    return displayFields
      .filter((displayField) => {
        if (isSharedCollection && displayField.name.startsWith('metadata.')) {
          const field = displayField.name.split('.');
          return shareMetadataFields.some(
            (item) =>
              item.name.toLocaleLowerCase() === field?.[1]?.toLocaleLowerCase(),
          );
        }
        if (displayField.name.startsWith('metadata.')) {
          return true;
        }
        const systemField = SEARCH_SYSTEM_FIELDS_BY_NAME[displayField.name];
        if (!systemField) {
          return false;
        }
        return systemField.viewable !== false;
      })
      .map((displayField) => {
        if (displayField.name.startsWith('metadata.')) {
          return displayField;
        }
        const systemField = SEARCH_SYSTEM_FIELDS_BY_NAME[displayField.name];

        const result: SearchField = {
          ...systemField,
          width: {
            ...systemField.width,
            current: displayField.width?.current,
          },
        };
        return result;
      });
  },
);

export const selectVisibleSearchFieldNames = createSelector(
  selectVisibleSearchFields,
  (searchFields) => searchFields.map(({ name }) => name),
);

export const selectVisibleSearchFieldNamesSet = createSelector(
  selectVisibleSearchFieldNames,
  (searchFieldNames) => new Set(searchFieldNames),
);

const getBreakpoint = (state: AppState) => state.displayReducer.breakpoint;

const searchMobileFields = new Set([
  FIELD_KEYFRAMES,
  FIELD_TITLE,
  FIELD_MEDIA_TYPE,
]);

export const selectRenderColumns = createSelector(
  selectVisibleSearchFields,
  getSearchDisplayFields,
  getBreakpoint,
  (visibleSearchFields, searchDisplayFields, breakpoint) => {
    let resultFields = visibleSearchFields;

    if (breakpoint <= EXTRASMALL_SCREEN) {
      resultFields = resultFields.filter((field) =>
        searchMobileFields.has(field.name),
      );
    }

    if (!searchDisplayFields) {
      return resultFields;
    }

    const fieldsDict = keyBy(searchDisplayFields, 'name');

    const renderColumns: Column<SearchItemEntity>[] = resultFields.map(
      (field) => ({
        ...field,
        width: {
          min: field.width?.min,
          max: field.width?.max,
          fixed: field.width?.fixed,
          current:
            fieldsDict[field.name]?.width?.current ?? field.width?.current,
          autoResize: field.width?.autoResize,
        },
      }),
    );

    const thumbnailIndex = renderColumns.findIndex(
      (column) => column.name === FIELD_KEYFRAMES,
    );
    if (thumbnailIndex >= 0) {
      renderColumns[thumbnailIndex].hideLabel = true;
    }

    return renderColumns;
  },
);
